import { supabase } from './supabase'

const COOKIE_NAME = 'printhq_affiliate_referral'
const CLICK_WINDOW_SECONDS = 60 * 24 * 60 * 60

interface StoredReferral {
  code: string
  clickedAt: string
}

const normalizeCode = (value: string) => value.trim().toUpperCase().replace(/[^A-Z0-9_-]/g, '')

export function captureAffiliateLink(value: unknown) {
  if (typeof value !== 'string') return
  const code = normalizeCode(value)
  if (!/^[A-Z0-9_-]{3,40}$/.test(code)) return
  const referral: StoredReferral = { code, clickedAt: new Date().toISOString() }
  const secure = location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(referral))}; Max-Age=${CLICK_WINDOW_SECONDS}; Path=/; SameSite=Lax${secure}`
}

export function readAffiliateLink(): StoredReferral | null {
  const value = document.cookie.split('; ').find((part) => part.startsWith(`${COOKIE_NAME}=`))?.slice(COOKIE_NAME.length + 1)
  if (!value) return null
  try {
    const referral = JSON.parse(decodeURIComponent(value)) as StoredReferral
    if (!referral.code || !referral.clickedAt) return null
    if (Date.now() - new Date(referral.clickedAt).getTime() > CLICK_WINDOW_SECONDS * 1000) {
      clearAffiliateLink()
      return null
    }
    return referral
  } catch {
    clearAffiliateLink()
    return null
  }
}

export function clearAffiliateLink() {
  const secure = location.protocol === 'https:' ? '; Secure' : ''
  document.cookie = `${COOKIE_NAME}=; Max-Age=0; Path=/; SameSite=Lax${secure}`
}

/** Lock the last valid referral link to the signed-in account. */
export async function claimStoredAffiliateLink() {
  const referral = readAffiliateLink()
  if (!referral) return null
  const { data, error } = await supabase.rpc('claim_affiliate_attribution' as never, {
    p_code: referral.code,
    p_source: 'link',
    p_clicked_at: referral.clickedAt,
  } as never)
  if (error) throw error
  const result = data as { ok: boolean; code?: string; message?: string } | null
  if (result?.ok || ['invalid_code', 'expired_link', 'self_referral', 'already_paid', 'locked_conflict'].includes(result?.code || '')) {
    clearAffiliateLink()
  }
  return result
}
